import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import { formatLatexPreview } from "../../../../components/inputs/FractionInput";

// ==== FORMATOWANIE FRAKCJI ====

function formatFraction(numerator, denominator) {
  const gcd = (a, b) => {
    if (!Number.isFinite(a) || !Number.isFinite(b)) return 1;
    return b === 0 ? Math.abs(a) : gcd(b, a % b);
  };

  const sign = Math.sign(numerator) * Math.sign(denominator);
  const absNum = Math.abs(numerator);
  const absDen = Math.abs(denominator);
  const d = gcd(absNum, absDen);

  const simpNum = absNum / d;
  const simpDen = absDen / d;

  if (simpNum % simpDen === 0) {
    return `${sign * (simpNum / simpDen)}`;
  }

  const whole = Math.floor(simpNum / simpDen);
  const remainder = simpNum % simpDen;

  if (whole === 0) {
    return `${sign < 0 ? "-" : ""}\\dfrac{${simpNum}}{${simpDen}}`;
  }

  return `${sign < 0 ? "-" : ""}${whole}\\dfrac{${remainder}}{${simpDen}}`;
}


// ==== GENERATOR ====

export function generateTask() {
  const rand = () => Math.floor(Math.random() * 17) - 8;

  let x1, y1, x2, y2;

  do {
    x1 = rand();
    y1 = rand();
    x2 = rand();
    y2 = rand();
  } while (x1 === x2);

  const dx = x2 - x1;
  const dy = y2 - y1;

  return {
    type: "LinearSlopeTask",
    data: {
      x1, y1, x2, y2,
      slope: { numerator: dy, denominator: dx }
    }
  };
}


// ==== RENDER TASK ====

export function renderTask({ data }) {
  const { x1, y1, x2, y2 } = data;

  return (
    <>
      <p>
        Funkcja liniowa <InlineMath math="f(x)=ax+b" /> przechodzi przez punkty:
      </p>

      <p>
        <InlineMath math={`A=(${x1},${y1})`} />,{" "}
        <InlineMath math={`B=(${x2},${y2})`} />
      </p>

      <p>
        Oblicz współczynnik kierunkowy <InlineMath math="a" /> tej funkcji.
      </p>
    </>
  );
}


// ==== INPUT ====

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="a"
    />
  );
}


// ==== CHECK ====

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;

  const given = userAnswer.parsed;
  const expected = data.slope.numerator / data.slope.denominator;

  if (given === null) return false;

  return Math.abs(expected - given) < 0.01;
}


// ==== USER ANSWER ====

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  return <InlineMath math={formatLatexPreview(userAnswer.raw, "a")} />;
}


// ==== ANSWER ====

export function renderAnswer({ data }) {
  const { numerator, denominator } = data.slope;

  const gcd = (a, b) => {
    if (!Number.isFinite(a) || !Number.isFinite(b)) return 1;
    return b === 0 ? Math.abs(a) : gcd(b, a % b);
  };

  const d = gcd(numerator, denominator);
  const simpNum = numerator / d;
  const simpDen = denominator / d;

  return <InlineMath math={`a = ${formatFraction(simpNum, simpDen)}`} />;
}


// ==== EXPLANATION ====

export function renderExplanation({ data }) {
  const { x1, y1, x2, y2 } = data;

  const dx = x2 - x1;
  const dy = y2 - y1;

  const fs = (v) => (v < 0 ? `(${v})` : v);

  const gcd = (a, b) => {
    if (!Number.isFinite(a) || !Number.isFinite(b)) return 1;
    return b === 0 ? Math.abs(a) : gcd(b, a % b);
  };

  const d = gcd(dy, dx);
  const simpNum = dy / d;
  const simpDen = dx / d;

  return (
    <div className="space-y-2">

      <p><strong>Krok 1.</strong> Korzystamy ze wzoru na współczynnik kierunkowy funkcji liniowej przechodzącej przez dwa punkty:</p>

      <BlockMath math="a = \dfrac{y_2 - y_1}{x_2 - x_1}" />

      <p><strong>Krok 2.</strong> Podstawiamy dane:</p>

      <BlockMath
        math={`a = \\dfrac{${fs(y2)} - ${fs(y1)}}{${fs(x2)} - ${fs(x1)}} = \\dfrac{${dy}}{${dx}}`}
      />

      <p><strong>Krok 3.</strong> Upraszczamy ułamek:</p>

      <BlockMath math={`a = ${formatFraction(simpNum, simpDen)}`} />

    </div>
  );
}